import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Checkbox, Empty, ErrorState, Input, Ltr, Tabs, useConfirm, useDebounced, useToast } from './ui';

type Bucket = 'high' | 'medium' | 'low' | 'none';
type Proposal = {
  key: string;
  description: string;
  kind: 'product' | 'none' | 'ignore';
  confidence: 'high' | 'medium' | 'low';
  lines: number;
  lastPrice: number | null;
  lastDate: string | null;
  lastCustomer: string | null;
  product: { id: number; sku: string; name: string; description: string | null; basePrice: number | null } | null;
  hint: { tone: 'ok' | 'warn'; text: string } | null;
};
type Response = { counts: Record<Bucket, number>; lineCounts: Record<Bucket, number>; items: Proposal[] };

const TABS: Array<{ key: Bucket; label: string }> = [
  { key: 'high', label: 'בטוחות' },
  { key: 'medium', label: 'סבירות' },
  { key: 'low', label: 'לבדיקה' },
  { key: 'none', label: 'אין מוצר בקטלוג' },
];

/**
 * Bulk approval of suggested matches. Keyboard: ↑/↓ move, Enter approve, Delete reject,
 * N "not a catalog product".
 */
export function MatchProposals({ onEmpty }: { onEmpty?: () => void }) {
  const [bucket, setBucket] = useState<Bucket>('high');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const dq = useDebounced(q);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data, error, refetch } = useQuery({
    queryKey: ['match-proposals', bucket, dq],
    queryFn: () => api<Response>(`/api/products/matching/proposals${qs({ confidence: bucket, q: dq })}`),
    placeholderData: (prev) => prev,
  });
  const items = useMemo(() => data?.items ?? [], [data]);
  useEffect(() => {
    setSelected(new Set());
    setActive(0);
  }, [bucket, dq]);
  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : null;
  useEffect(() => {
    if (total === 0) onEmpty?.();
  }, [total, onEmpty]);

  const decide = async (keys: string[], action: 'accept' | 'reject' | 'ignore', label?: string) => {
    if (!keys.length) return;
    setBusy(true);
    try {
      const r = await api<{ decided: number; linked: number }>('/api/products/matching/proposals/decide', { body: { keys, action } });
      toast.success(label ?? (action === 'accept' ? `${fmtNumber(r.decided)} אושרו` : action === 'ignore' ? `${fmtNumber(r.decided)} סומנו כלא מהקטלוג` : `${fmtNumber(r.decided)} הוחזרו לשיוך ידני`));
      setSelected(new Set());
      for (const k of ['match-proposals', 'product-matching', 'products', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const approveAll = async () => {
    const ok = await confirm({
      title: `לאשר את כל ${fmtNumber(items.length)} ההצעות ב"${TABS.find((t) => t.key === bucket)!.label}"?`,
      body:
        bucket === 'none'
          ? 'התיאורים יסומנו "לא מהקטלוג" ולא יופיעו יותר בשיוך. אפשר לשנות כל אחד אחר כך במסך השיוך.'
          : `השורות יקושרו למוצרים המוצעים (${fmtNumber(data?.lineCounts[bucket] ?? 0)} שורות במסמכים). אפשר לשנות כל שיוך אחר כך במסך השיוך.`,
      confirmLabel: 'אישור הכל',
    });
    if (ok) await decide(items.map((i) => i.key), bucket === 'none' ? 'ignore' : 'accept');
  };

  // Keyboard review: one row at a time.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName) || busy || !items.length) return;
      const it = items[Math.min(active, items.length - 1)];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => Math.min(items.length - 1, a + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter' && it) {
        e.preventDefault();
        void decide([it.key], it.kind === 'product' ? 'accept' : 'ignore', 'אושר');
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && it) {
        e.preventDefault();
        void decide([it.key], 'reject', 'הוחזר לשיוך ידני');
      } else if ((e.key === 'n' || e.key === 'N' || e.key === 'מ') && it) {
        e.preventDefault();
        void decide([it.key], 'ignore', 'סומן כלא מהקטלוג');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => {
    document.getElementById(`prop-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (!data) return <div className="panel-body"><span className="spinner" /></div>;
  if (total === 0) return <Empty title="אין הצעות שממתינות לאישור" body="כל ההצעות טופלו. מה שנשאר — במסך השיוך הידני." />;

  const allSel = items.length > 0 && items.every((i) => selected.has(i.key));

  return (
    <div className="stack" style={{ gap: 12 }}>
      <Callout tone="info">
        כל תיאור שורה מהחשבוניות קיבל הצעה אחרי השוואה של שם, צבע/גודל ומחיר מול הקטלוג. מאשרים בבת אחת או אחד-אחד —
        שום דבר לא מקושר בלי אישור. מקלדת: <strong>↑↓</strong> מעבר · <strong>Enter</strong> אישור · <strong>Delete</strong> לא נכון (לשיוך ידני) · <strong>N</strong> לא מהקטלוג.
      </Callout>
      <Tabs<Bucket> value={bucket} onChange={setBucket} tabs={TABS.map((t) => ({ ...t, count: data.counts[t.key] }))} />
      <section className="panel">
        <div className="panel-head row wrap" style={{ gap: 8 }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש" style={{ maxWidth: 260 }} aria-label="חיפוש" />
          <span className="small muted">{fmtNumber(items.length)} תיאורים · {fmtNumber(data.lineCounts[bucket])} שורות</span>
          <span className="spacer" />
          {selected.size ? (
            <>
              <Button size="sm" variant="primary" loading={busy} onClick={() => decide([...selected], bucket === 'none' ? 'ignore' : 'accept')}>
                אישור {fmtNumber(selected.size)} מסומנים
              </Button>
              <Button size="sm" onClick={() => decide([...selected], 'reject')} disabled={busy}>לא נכון — לשיוך ידני</Button>
            </>
          ) : (
            <Button size="sm" variant="primary" loading={busy} onClick={approveAll} disabled={!items.length}>
              {bucket === 'none' ? `סימון כל ${fmtNumber(items.length)} כ"לא מהקטלוג"` : `אישור כל ${fmtNumber(items.length)} ההצעות`}
            </Button>
          )}
        </div>
        <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <Checkbox checked={allSel} onChange={(v) => setSelected(v ? new Set(items.map((i) => i.key)) : new Set())}>
                    <span className="sr-only">בחירת הכל</span>
                  </Checkbox>
                </th>
                <th>תיאור בחשבוניות</th>
                <th className="num">מחיר בשורה</th>
                <th>מוצר מוצע בקטלוג</th>
                <th className="num">מחיר בקטלוג</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr
                  key={it.key}
                  id={`prop-${i}`}
                  onClick={() => setActive(i)}
                  style={i === active ? { background: 'var(--accent-soft)' } : undefined}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(it.key)}
                      onChange={(v) => {
                        const n = new Set(selected);
                        if (v) n.add(it.key);
                        else n.delete(it.key);
                        setSelected(n);
                      }}
                    >
                      <span className="sr-only">בחירה</span>
                    </Checkbox>
                  </td>
                  <td className="primary" style={{ maxWidth: 340 }}>
                    {it.description}
                    <span className="sub">
                      {fmtNumber(it.lines)} שורות{it.lastCustomer ? ` · ${it.lastCustomer}` : ''}{it.lastDate ? ` · ${fmtDate(it.lastDate)}` : ''}
                    </span>
                  </td>
                  <td className="num tabular">{fmtMoney(it.lastPrice)}</td>
                  <td style={{ maxWidth: 340 }}>
                    {it.product ? (
                      <>
                        <span className="strong">{it.product.name}</span>
                        {it.product.description ? <span className="muted"> — {it.product.description}</span> : null}
                        <span className="sub">
                          <Ltr>{it.product.sku}</Ltr>
                          {it.hint ? <span style={{ color: it.hint.tone === 'ok' ? 'var(--ok)' : 'var(--warn)' }}> · {it.hint.text}</span> : null}
                        </span>
                      </>
                    ) : (
                      <span className="muted">{it.kind === 'ignore' ? 'לא שורת מוצר (סיכום / הנחה / הערה)' : 'אין מוצר כזה בקטלוג'}</span>
                    )}
                  </td>
                  <td className="num tabular">{it.product ? fmtMoney(it.product.basePrice) : <span className="faint">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide([it.key], it.kind === 'product' ? 'accept' : 'ignore', 'אושר')}>אישור</Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide([it.key], 'reject', 'הוחזר לשיוך ידני')} title="ההצעה לא נכונה — לבחור ידנית">✕</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 ? <tr><td colSpan={6} className="muted">אין הצעות בקבוצה הזו.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
